import type * as THREE from 'three';
import { pickShow } from '../content/tv';
import { byBody, lookLabel } from '../content/looks';
import { BOTTOM_STYLES, HAT_STYLES, SHOE_STYLES, TOP_STYLES, type CharacterLook } from '../state/types';
import { TvStage } from '../stages/TvStage';
import type { Interactions } from '../systems/Interactions';
import { isShopOpen, shopHoursText, type ShopKind } from '../ui/ShopUI';
import { SPOTS, type Vec2 } from '../world/layout';
import type { PublicRoom } from '../world/PublicRoom';
import {
  BOOZE_SPOTS,
  buildBoozeInterior,
  buildGasInterior,
  buildLaundryInterior,
  buildPawnInterior,
  GAS_SPOTS,
  LAUNDRY_SPOTS,
  PAWN_SPOTS,
  type GasStore,
  type Laundromat,
  type ShopRoom,
} from '../world/shops';
import {
  buildCooterInterior,
  buildParkOfficeInterior,
  COOTER_SPOTS,
  OFFICE_SPOTS,
  type CooterOffice,
  type Office,
} from '../world/officeRooms';
import type { Activities } from './Activities';
import type { Interiors } from './Interiors';
import type { GameServices } from './services';

const HAT_COLORS = ['#c62828', '#1d4e9e', '#2f7d32', '#1b1b1b', '#e8c55a', '#ff6fa8', '#f4f4f0'];

const next = <T>(list: readonly T[], cur: T): T => list[(list.indexOf(cur) + 1) % list.length] as T;

interface ShopDoor {
  kind: ShopKind | 'laundry';
  name: string;
  door: Vec2;
  room: PublicRoom;
}

/**
 * The shops on the strip, from the inside: you walk in the door and buy from whoever's
 * behind the glass (or the bars, or the chicken wire). The laundromat has its own
 * things to do: wash your clothes, dig through the lost and found, watch its TV.
 */
export class Shops {
  readonly gas: GasStore;
  readonly pawn: ShopRoom;
  readonly booze: ShopRoom;
  readonly laundry: Laundromat;
  readonly office: Office;
  readonly cooter: CooterOffice;
  private readonly doors: ShopDoor[];
  private washing = -1;

  constructor(
    private readonly s: GameServices,
    private readonly interiors: Interiors,
    private readonly act: Activities,
  ) {
    const { dynamic, collision } = s.world;
    this.gas = buildGasInterior(dynamic, collision);
    this.pawn = buildPawnInterior(dynamic, collision);
    this.booze = buildBoozeInterior(dynamic, collision);
    this.laundry = buildLaundryInterior(dynamic, collision);
    this.office = buildParkOfficeInterior(dynamic, collision);
    this.cooter = buildCooterInterior(dynamic, collision);
    const cooterDoor = { x: SPOTS.cooterOffice.x + 2.6, z: SPOTS.cooterOffice.z - 2.6 };
    this.doors = [
      { kind: 'gas', name: 'the Gas-N-Grub', door: SPOTS.gasDoor, room: this.gas.room },
      { kind: 'pawn', name: "Lucky's Pawn & Payday", door: SPOTS.pawnDoor, room: this.pawn.room },
      { kind: 'booze', name: 'the Booze Barn', door: SPOTS.boozeDoor, room: this.booze.room },
      { kind: 'laundry', name: 'Suds-N-Duds', door: SPOTS.laundromatDoor, room: this.laundry.room },
      { kind: 'office', name: 'the park office', door: SPOTS.office, room: this.office.room },
      { kind: 'cooter', name: "Cooter's office", door: cooterDoor, room: this.cooter.room },
    ];
    // You come back out a step in front of the door, on the parking lot side.
    for (const d of this.doors) {
      const out =
        d.kind === 'office'
          ? { x: d.door.x + 0.8, z: d.door.z }
          : d.kind === 'cooter'
            ? { x: d.door.x + 0.6, z: d.door.z + 0.6 }
            : { x: d.door.x, z: d.door.z - 0.8 };
      interiors.addPublic(d.room, out);
    }
  }

  private open(kind: ShopKind | 'laundry'): boolean {
    // The laundromat's 24 hours, like the sign says.
    return kind === 'laundry' || isShopOpen(kind, this.s.store.minutes);
  }

  private hours(kind: ShopKind | 'laundry'): string {
    return kind === 'laundry' ? 'all night' : shopHoursText(kind);
  }

  register(interactions: Interactions): void {
    const { store, ui, rng, npcs } = this.s;
    const add = interactions.add.bind(interactions);

    for (const d of this.doors) {
      add({
        id: `shop-door-${d.kind}`,
        at: () => d.door,
        radius: 2,
        prompt: () =>
          this.open(d.kind) ? `Go into ${d.name}` : `${d.name} (closed, open ${this.hours(d.kind)})`,
        action: () => {
          if (!this.open(d.kind)) {
            store.toast(
              `Locked. A sign on the door says "GONE FISHIN'." Open ${this.hours(d.kind)}.`,
              'info',
              ':lock:',
            );
            return;
          }
          this.interiors.enterPublic(d.room.slot);
        },
        priority: 0.5,
        icon: ':door:',
      });
      add({
        id: `shop-exit-${d.kind}`,
        at: () => d.room.exit,
        radius: 1.2,
        prompt: () => 'Head outside',
        action: () => this.interiors.leave(),
        priority: 0.5,
        icon: ':door:',
      });
    }

    // Buying through the glass.
    const counter = (
      kind: ShopKind,
      room: PublicRoom,
      window: Vec2,
      clerk: string,
      verb: string,
      glass: THREE.Object3D,
    ): void =>
      add({
        id: `shop-counter-${kind}`,
        at: () => room.world(window),
        radius: 1.3,
        prompt: () => {
          const npc = npcs.get(clerk);
          return npc?.active ? `${verb} (${npc.def.name}, through the glass)` : 'Nobody at the register';
        },
        action: () => {
          if (!npcs.get(clerk)?.active) return;
          this.s.sfx('ding');
          ui.shop.open(kind);
        },
        priority: 1,
        icon: ':cart:',
        highlight: () => glass,
      });
    counter('gas', this.gas.room, GAS_SPOTS.window, 'raylene', 'Buy something', this.gas.glass);
    counter('pawn', this.pawn.room, PAWN_SPOTS.window, 'lucky', "See what Lucky's got", this.pawn.window);
    counter('booze', this.booze.room, BOOZE_SPOTS.window, 'dottie', 'Ring it up', this.booze.window);
    counter(
      'cooter',
      this.cooter.room,
      COOTER_SPOTS.window,
      'cooter',
      'Talk business: buy or swap a ride',
      this.cooter.window,
    );
    add({
      id: 'office-slot',
      at: () => this.office.room.world({ x: OFFICE_SPOTS.slot.x, z: OFFICE_SPOTS.slot.z + 0.9 }),
      radius: 1.3,
      prompt: () => 'Rent slot (Darlene is "back in 5 minutes")',
      action: () => {
        this.s.sfx('ding');
        ui.shop.open('office');
      },
      priority: 1,
      icon: ':trailer:',
      highlight: () => this.office.slot,
    });

    // --- the Gas-N-Grub's little extras ---------------------------------------------------
    add({
      id: 'gas-grill',
      at: () => this.gas.room.world({ x: GAS_SPOTS.grill.x, z: GAS_SPOTS.grill.z + 0.3 }),
      radius: 1.3,
      prompt: () => 'Grab a roller dog ($1.50)',
      action: () => {
        if (!store.spend(1.5)) {
          store.toast('A dollar fifty. Check the car seats.', 'bad', ':hotdog:');
          return;
        }
        store.give('hotdog', 1);
        this.s.sfx('pickup');
        store.toast(
          'You fish a dog off the rollers with the tongs. It has been rolling since a previous administration.',
          'meme',
          ':hotdog:',
        );
      },
      icon: ':hotdog:',
      highlight: () => this.gas.dogs,
    });
    add({
      id: 'gas-slushie',
      at: () => this.gas.room.world({ x: GAS_SPOTS.slushie.x - 0.2, z: GAS_SPOTS.slushie.z + 0.9 }),
      radius: 1.3,
      prompt: () => 'Pour a Polar Pop slushie ($1)',
      action: () => {
        if (!store.spend(1)) return;
        store.incr('slushies');
        this.s.sfx('gulp');
        this.s.player.doBusy({
          pose: 'drink',
          until: this.s.now() + 1.6,
          onDone: () => {
            store.apply({ stats: { energy: 12, buzz: -4 } });
            store.toast(
              rng.pick([
                'BRAIN FREEZE. You see God. God is also cold.',
                'Blue raspberry. Your tongue will be blue until Thursday.',
              ]),
              'meme',
              ':ice:',
            );
          },
        });
      },
      icon: ':mtn-dewd:',
    });
    add({
      id: 'gas-atm',
      at: () => this.gas.room.world({ x: 3.6, z: 3.5 }),
      radius: 1.1,
      prompt: () => 'Check your balance at the ATM ($3 fee)',
      action: () => {
        store.spend(Math.min(3, store.state.money));
        this.s.sfx('error');
        store.toast(
          `The ATM charges $3 to tell you that you have $${store.state.money.toFixed(2)}. It does not dispense cash. It never has.`,
          'meme',
          ':atm:',
        );
      },
      icon: ':atm:',
    });

    // --- the pawn shop ----------------------------------------------------------------------
    add({
      id: 'pawn-guitar',
      at: () => this.pawn.room.world({ x: -4.4, z: 1.9 }),
      radius: 1.4,
      prompt: () => (store.doneToday('pawn_mower') ? null : 'Try to start the used mower'),
      action: () => {
        store.markToday('pawn_mower');
        this.s.sfx('clank');
        store.addClout(1);
        store.toast(
          'Six pulls. On the seventh it roars, coughs blue smoke, and dies. Lucky yells "YOU BREAK IT YOU BOUGHT IT." It was already broke.',
          'meme',
          ':axe:',
        );
      },
      icon: ':wrench:',
    });

    // --- the Booze Barn ---------------------------------------------------------------------
    add({
      id: 'booze-cooler',
      at: () => this.booze.room.world({ x: BOOZE_SPOTS.cooler.x, z: BOOZE_SPOTS.cooler.z + 0.7 }),
      radius: 1.6,
      prompt: () => (store.doneToday('walkin') ? null : 'Stand in the walk-in cooler a minute'),
      action: () => {
        store.markToday('walkin');
        const heat = store.flag('heatwave') === store.day;
        store.apply({ stats: { energy: heat ? 20 : 8, buzz: -6 }, minutes: 10 });
        store.toast(
          heat
            ? 'Thirty-eight degrees of pure salvation. Dottie lets you stay because it is 104 outside and she is not a monster.'
            : `You stand among the 30-racks like a ${byBody(store.state.player.look, 'man', 'woman')} at peace. Dottie bangs on the glass.`,
          'good',
          ':ice:',
        );
      },
      icon: ':ice:',
    });

    // --- Suds-N-Duds --------------------------------------------------------------------------
    const L = this.laundry;
    const inL = (p: Vec2) => () => L.room.world(p);
    add({
      id: 'laundry-wash',
      at: inL({ x: LAUNDRY_SPOTS.washer.x, z: LAUNDRY_SPOTS.washer.z + 0.9 }),
      radius: 1.6,
      prompt: () => (store.flag('fresh') === store.day ? null : 'Wash a load ($3, fresh all day)'),
      action: () => this.wash(),
      icon: ':laundry:',
    });
    add({
      id: 'laundry-dryers',
      at: inL({ x: LAUNDRY_SPOTS.dryer.x, z: LAUNDRY_SPOTS.dryer.z + 0.9 }),
      radius: 1.4,
      prompt: () => (store.doneToday('dryer_coins') ? null : 'Check the dryers for loose change'),
      action: () => this.checkDryers(),
      icon: ':coin:',
    });
    add({
      id: 'laundry-lostfound',
      at: inL({ x: LAUNDRY_SPOTS.lostFound.x - 0.9, z: LAUNDRY_SPOTS.lostFound.z }),
      radius: 1.3,
      prompt: () => 'Dig through the Lost & Found (new clothes, $5)',
      action: () => this.lostAndFound(),
      icon: ':tshirt:',
    });
    add({
      id: 'laundry-tv',
      at: inL(LAUNDRY_SPOTS.tvSeat),
      radius: 1.2,
      prompt: () => 'Sit and watch the laundromat TV',
      action: () => this.watchLaundryTv(),
      icon: ':tv:',
    });
    add({
      id: 'laundry-window',
      at: inL({ x: LAUNDRY_SPOTS.window.x, z: LAUNDRY_SPOTS.window.z + 0.2 }),
      radius: 1.3,
      prompt: () =>
        npcs.get('opal')?.active ? 'Talk to Miss Opal (through the bars)' : 'Nobody at the window',
      action: () => {
        const opal = npcs.get('opal');
        if (opal?.active) this.act.talk(opal);
      },
      priority: 1,
      icon: ':speech:',
      highlight: () => this.laundry.window,
    });
    add({
      id: 'laundry-vending',
      at: inL({ x: LAUNDRY_SPOTS.vending.x + 0.9, z: LAUNDRY_SPOTS.vending.z }),
      radius: 1.3,
      prompt: () => 'Vending machine',
      action: () =>
        ui.modal.show({
          title: 'Snak-O-Matic',
          art: ':candy-bar:',
          text: 'The glass is cloudy. Something in row C has been there since the Clinton administration.',
          choices: [
            {
              label: 'Pork rinds',
              hint: '-$1.50',
              requires: ({ store: st }) => st.canAfford(1.5) || 'Need $1.50',
              run: ({ store: st }) => {
                st.spend(1.5);
                st.give('porkRinds', 1);
                return "Ka-chunk. The bag drops. It was already open. That's fine.";
              },
            },
            {
              label: 'Mtn Dewd',
              hint: '-$2.50',
              requires: ({ store: st }) => st.canAfford(2.5) || 'Need $2.50',
              run: ({ store: st, rng: r }) => {
                st.spend(2.5);
                if (r.chance(0.2))
                  return 'The machine takes your money and gives you nothing but a sound. A mean sound.';
                st.give('dewd', 1);
                return 'It drops. It is warm. It is still Mtn Dewd.';
              },
            },
          ],
          leave: 'Walk away',
        }),
      icon: ':candy-bar:',
    });
  }

  /** Per frame: a washer spins while your load's in. */
  update(): void {
    const drum = this.laundry.drums[0];
    if (drum && this.washing > this.s.now()) drum.rotation.y += 0.4;
  }

  private wash(): void {
    const { store, player } = this.s;
    if (!this.act.canAct()) return;
    if (!store.spend(3)) {
      store.toast('Three bucks in quarters. Miss Opal can make change. Grudgingly.', 'bad', ':laundry:');
      return;
    }
    this.s.sfx('door');
    this.washing = this.s.now() + 3;
    player.doBusy({
      pose: 'push',
      until: this.s.now() + 2.8,
      onDone: () => {
        store.setFlag('fresh', store.day);
        store.incr('laundry');
        store.markToday('laundry');
        store.apply({ clout: 2, minutes: 45 });
        store.toast(
          'Wash, dry, and a sniff test you pass. Fresh clothes: +1 Clout on everything for the rest of the day.',
          'good',
          ':laundry:',
        );
      },
    });
  }

  private checkDryers(): void {
    const { store, rng } = this.s;
    store.markToday('dryer_coins');
    this.s.sfx('clank');
    const roll = rng.next();
    if (roll < 0.15) {
      store.toast('Nothing but lint and a single sock that you decide is yours now.', 'info', ':sock:');
      return;
    }
    const cash = roll > 0.93 ? 20 : Math.round(rng.range(0.25, 4) * 4) / 4;
    store.earn(cash);
    store.incr('dryerCoins');
    store.toast(
      cash >= 20
        ? 'A folded twenty in the lint trap. Somebody is having a worse day than you.'
        : `$${cash.toFixed(2)} in quarters in the back of dryer four. Warm money.`,
      'good',
      ':coin:',
    );
  }

  /** Swap clothes out of the lost and found box: five bucks for all you can carry. */
  private lostAndFound(): void {
    const { store } = this.s;
    if (!store.doneToday('lost_found')) {
      if (!store.spend(5)) {
        store.toast('Five bucks to dig. Miss Opal runs a business.', 'bad', ':tshirt:');
        return;
      }
      store.markToday('lost_found');
      store.incr('lostFound');
    }
    this.wardrobe();
  }

  private wardrobe(): void {
    const { store, ui } = this.s;
    const look = store.state.player.look;
    const change = (patch: Partial<CharacterLook>, line: string) => () => {
      this.s.setLook({ ...store.state.player.look, ...patch });
      this.s.sfx('pickup');
      window.setTimeout(() => this.wardrobe(), 0);
      void line;
    };
    ui.modal.show({
      title: 'Lost & Found',
      art: ':tshirt:',
      text: "A cardboard box of other people's clothes. Some still warm. You've paid for the day: take what fits, and some of what doesn't.",
      choices: [
        {
          label: `:tshirt: Top: ${lookLabel.top(look.top, look)} → ${lookLabel.top(next(TOP_STYLES, look.top), look)}`,
          run: change({ top: next(TOP_STYLES, look.top) }, 'shirt'),
        },
        {
          label: `:jeans: Bottoms: ${lookLabel.bottom(look.bottom, look)} → ${lookLabel.bottom(next(BOTTOM_STYLES, look.bottom), look)}`,
          run: change({ bottom: next(BOTTOM_STYLES, look.bottom) }, 'pants'),
        },
        {
          label: `:croc: Shoes: ${lookLabel.shoes(look.shoes)} → ${lookLabel.shoes(next(SHOE_STYLES, look.shoes))}`,
          run: change({ shoes: next(SHOE_STYLES, look.shoes) }, 'shoes'),
        },
        {
          label: `:cap: Hat: ${lookLabel.hat(look.hat)} → ${lookLabel.hat(next(HAT_STYLES, look.hat))}`,
          run: change({ hat: next(HAT_STYLES, look.hat) }, 'hat'),
        },
        {
          label: ':paint: A different color hat',
          requires: () => look.hat !== 'none' || 'Find a hat first',
          run: change({ hatColor: next(HAT_COLORS, look.hatColor) }, 'hat color'),
        },
        {
          label: look.shades ? ':shades: Lose the shades' : ':shades: Gas station shades',
          run: change({ shades: !look.shades }, 'shades'),
        },
      ],
      leave: "That's the look",
    });
  }

  private watchLaundryTv(): void {
    const { store, rng, player, ui } = this.s;
    if (!this.act.canAct()) return;
    const seat = this.laundry.room.world(LAUNDRY_SPOTS.tvSeat);
    const stage = new TvStage(
      { screen: this.laundry.tv, seat, facing: Math.PI / 2, beer: false },
      player,
      () => pickShow(rng, false),
      rng,
      (n) => this.s.sfx(n),
    );
    ui.stages.open(stage, (st) => {
      const tv = st as TvStage;
      store.incr('tv', tv.shows);
      if (tv.murray) store.incr('murray');
      store.apply({ stats: { energy: Math.min(30, 8 * tv.shows) }, minutes: 30 * tv.shows });
      return null;
    });
  }
}
